import { useEffect, useRef, useState } from 'react';

import { ITEM_AVAILABILITY_STATUS } from 'features/Sku/Items/SkuItems.constants';

import { namedOptions } from './Modifiers.formState';
import { deleteWouldBreakRule, unsatisfiableRule } from './Modifiers.saveGuards';

export const BLOCKED_OPTION_ACTION = Object.freeze({
  DELETE: 'delete',
  AVAILABILITY: 'availability',
});

const statusesById = values =>
  new Map(namedOptions(values).map(option => [option.core?.id, option.availabilityStatus]));

export default function useOptionActionGuard({ values, remove }) {
  const [blocked, setBlocked] = useState(null);
  const previousStatusesRef = useRef(null);

  const guardedRemove = index => {
    if (deleteWouldBreakRule(values, index)) {
      setBlocked({ kind: BLOCKED_OPTION_ACTION.DELETE, optionId: values.modifiers[index]?.core?.id });
      return;
    }
    remove(index);
  };

  useEffect(() => {
    const current = statusesById(values);
    const previous = previousStatusesRef.current;
    previousStatusesRef.current = current;
    if (!previous || !unsatisfiableRule(values)) return;

    const downgradedId = [...current.keys()].find(
      id =>
        previous.get(id) === ITEM_AVAILABILITY_STATUS.AVAILABLE &&
        current.get(id) !== ITEM_AVAILABILITY_STATUS.AVAILABLE
    );
    if (downgradedId) setBlocked({ kind: BLOCKED_OPTION_ACTION.AVAILABILITY, optionId: downgradedId });
  });

  useEffect(() => {
    if (!blocked) return;

    if (blocked.kind === BLOCKED_OPTION_ACTION.AVAILABILITY) {
      if (!unsatisfiableRule(values)) setBlocked(null);
      return;
    }

    const index = (values.modifiers || []).findIndex(option => option.core?.id === blocked.optionId);
    if (index < 0 || !deleteWouldBreakRule(values, index)) setBlocked(null);
  });

  return { blocked, guardedRemove, dismissBlocked: () => setBlocked(null) };
}
